/**
 * Кабинет владельца портала (ТЗ 6.29–6.30): общие запросы и мелкие элементы страниц.
 * Доступен только людям с признаком «владелец портала» — API проверяет это сам.
 */
import {
  LICENSE_STATE_TITLES,
  LIMIT_KINDS,
  LIMIT_TITLES,
  type LicenseState,
  type Plan,
  type PlanLimits,
  type PlatformOverview,
  type TenantSummary,
} from '@epc/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Badge } from '../../ui/feedback';
import { STATE_TONE } from '../admin/LicensePage';

export const plansQuery = queryOptions({
  queryKey: ['platform', 'plans'],
  queryFn: () => api<Plan[]>('/platform/plans'),
});
export const tenantsQuery = queryOptions({
  queryKey: ['platform', 'tenants'],
  queryFn: () => api<TenantSummary[]>('/platform/tenants'),
});
export const overviewQuery = queryOptions({
  queryKey: ['platform', 'overview'],
  queryFn: () => api<PlatformOverview>('/platform/overview'),
});

export function StateBadge({ state }: { state: LicenseState }) {
  return <Badge tone={STATE_TONE[state]}>{LICENSE_STATE_TITLES[state]}</Badge>;
}

/** Лимиты тарифа одной строкой: «10 пользователей, 3 проекта, файлы 10 ГБ» */
export function limitsText(limits: PlanLimits): string {
  const parts: string[] = [];
  for (const k of LIMIT_KINDS) {
    const v = limits[k];
    if (v == null) continue;
    if (k === 'files_mb')
      parts.push(`файлы ${v >= 1024 ? `${Math.round((v / 1024) * 10) / 10} ГБ` : `${v} МБ`}`);
    else parts.push(`${LIMIT_TITLES[k].toLowerCase()}: ${v}`);
  }
  return parts.length ? parts.join(', ') : 'без ограничений';
}

/** Цена тарифа: null — «по договорённости» */
export function priceText(v: number | null, currency: string, period: string): string {
  if (v == null) return 'по договорённости';
  return `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ${currency}/${period}`;
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function plusDays(iso: string, days: number): string {
  const t = Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
  return new Date(t + days * 86_400_000).toISOString().slice(0, 10);
}

/** Поле лимита в форме: пусто — без ограничения */
export function parseLimit(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}
